/**
 * Set Initial Password Page Component
 * Allows newly approved users to set their password after admin approval
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Alert, Typography, Tooltip, Result, Spin } from 'antd';
import { LockOutlined, InfoCircleOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { setInitialPassword, validateSetPasswordToken } from '../../services/authService';
import PagesHeader from '../../PagesHeader';
import styles from './SetPassword.module.css';

const { Title, Text } = Typography;

/**
 * Password requirements for validation
 */
const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireDigit: true,
  requireSpecialChar: true,
};

/**
 * Set Password page - set initial password after approval
 */
const SetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);

  // Get token and userId from URL params
  const token = searchParams.get('token');
  const userId = searchParams.get('userId');

  // Validate URL params and token on mount
  useEffect(() => {
    const validateToken = async () => {
      // Check if URL params are present
      if (!token || !userId) {
        setInvalidLink(true);
        setValidatingToken(false);
        return;
      }

      try {
        // Validate the token with the backend
        const response = await validateSetPasswordToken(userId, token);
        if (!response.isValid) {
          setInvalidLink(true);
        }
      } catch (err) {
        // If validation fails, show invalid link
        console.warn('Token validation failed:', err.message);
        setInvalidLink(true);
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token, userId]);

  /**
   * Validate password meets requirements
   */
  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error(t('auth.passwordRequired')));
    }
    if (value.length < PASSWORD_REQUIREMENTS.minLength) {
      return Promise.reject(new Error(t('auth.passwordTooShort', { min: PASSWORD_REQUIREMENTS.minLength })));
    }
    if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(value)) {
      return Promise.reject(new Error(t('auth.passwordNeedsUppercase')));
    }
    if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(value)) {
      return Promise.reject(new Error(t('auth.passwordNeedsLowercase')));
    }
    if (PASSWORD_REQUIREMENTS.requireDigit && !/[0-9]/.test(value)) {
      return Promise.reject(new Error(t('auth.passwordNeedsDigit')));
    }
    if (PASSWORD_REQUIREMENTS.requireSpecialChar && !/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      return Promise.reject(new Error(t('auth.passwordNeedsSpecial')));
    }
    return Promise.resolve();
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setError(null);

    try {
      await setInitialPassword(userId, token, values.newPassword, values.confirmPassword);
      setSuccess(true);
    } catch (err) {
      if (err.message?.includes('expired') || err.message?.includes('invalid')) {
        setError(t('auth.setPasswordLinkExpired'));
      } else {
        setError(err.message || t('auth.setPasswordFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state while validating token
  if (validatingToken) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.card}>
            <div className={styles.loadingContainer}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
              <Text className={styles.loadingText}>
                {t('auth.validatingLink') || 'Validating link...'}
              </Text>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show invalid link message
  if (invalidLink) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.card}>
            <Result
              status="error"
              title={t('auth.invalidSetPasswordLink')}
              subTitle={t('auth.invalidSetPasswordLinkMessage')}
              extra={[
                <Link to="/login" key="login">
                  <Button type="primary" className={styles.submitButton}>
                    {t('auth.backToLogin')}
                  </Button>
                </Link>,
              ]}
            />
          </div>
        </main>
      </div>
    );
  }

  // Show success message after password set
  if (success) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.card}>
            <div className={styles.successContainer}>
              <CheckCircleOutlined className={styles.successIcon} />
              <Title level={3} className={styles.successTitle}>
                {t('auth.passwordSetSuccess')}
              </Title>
              <Text className={styles.successText}>
                {t('auth.passwordSetSuccessMessage')}
              </Text>
              <Link to="/login">
                <Button type="primary" size="large" className={styles.submitButton}>
                  {t('auth.signIn')}
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <PagesHeader />
      
      <main className={styles.main}>
        <div className={styles.card}>
          <div className={styles.header}>
            <Title level={2} className={styles.title}>
              {t('auth.setPasswordTitle')}
            </Title>
            <Text type="secondary" className={styles.subtitle}>
              {t('auth.setPasswordSubtitle')}
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              className={styles.alert}
            />
          )}

          <Form
            form={form}
            name="setPassword"
            onFinish={handleSubmit}
            layout="vertical"
            requiredMark={false}
            className={styles.form}
          >
            <Form.Item
              name="newPassword"
              label={
                <span>
                  {t('auth.newPassword')}
                  <Tooltip title={t('auth.passwordRequirements')}>
                    <InfoCircleOutlined className={styles.infoIcon} />
                  </Tooltip>
                </span>
              }
              rules={[{ validator: validatePassword }]}
            >
              <Input.Password
                prefix={<LockOutlined className={styles.inputIcon} />}
                placeholder={t('auth.newPasswordPlaceholder')}
                size="large"
                autoComplete="new-password"
                disabled={submitting}
              />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              dependencies={['newPassword']}
              rules={[
                { required: true, message: t('auth.confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('auth.passwordsMustMatch')));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className={styles.inputIcon} />}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                size="large"
                autoComplete="new-password"
                disabled={submitting}
              />
            </Form.Item>

            <div className={styles.passwordHint}>
              <Text type="secondary" className={styles.hintText}>
                {t('auth.passwordRequirements')}
              </Text>
            </div>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                className={styles.submitButton}
              >
                {submitting ? t('auth.settingPassword') : t('auth.setPasswordButton')}
              </Button>
            </Form.Item>
          </Form>

          <div className={styles.loginPrompt}>
            <Text type="secondary">
              {t('auth.alreadyHavePassword')}{' '}
            </Text>
            <Link to="/login" className={styles.loginLink}>
              {t('auth.signIn')}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SetPassword;
